import React, { useEffect, useState } from 'react';
import { appointmentService, Appointment, Professional, ServiceCategory, Service } from '../services/appointmentService';
import { clientService, Client } from '../services/client';
import { Calendar, Clock, DollarSign, User, Edit2, Trash2, X, Check, XCircle, Plus, Search } from 'lucide-react';

interface Props {
  branch?: 'antofagasta' | 'santiago';
}

const BookingsList: React.FC<Props> = ({ branch = 'antofagasta' }) => {
  const [bookings, setBookings] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'active' | 'history'>('active');
  const [selectedBooking, setSelectedBooking] = useState<Appointment | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    date: '',
    start_time: '',
    end_time: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
  });

  // Estados para crear reserva
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [createFormData, setCreateFormData] = useState({
    professional: '',
    date: '',
    start_time: '',
    end_time: '',
    services: [] as number[],
    customer_name: '',
    customer_phone: '',
    customer_email: '',
  });
  // Filtro por categoría de servicios para el modal
  const [categoryFilterId, setCategoryFilterId] = useState<number | null>(null);
  // Búsqueda de servicios por texto
  const [serviceSearchText, setServiceSearchText] = useState('');
  
  // Clientes frecuentes
  const [clients, setClients] = useState<Client[]>([]);
  const [clientSearchText, setClientSearchText] = useState('');
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadBookings();
  }, [viewMode, branch]);

  // Cargar datos auxiliares cuando se abre el modal de creación
  useEffect(() => {
    if (showCreateModal) {
      const fetchData = async () => {
        try {
          const [pros, cats, clientsData] = await Promise.all([
            branch === 'santiago' ? appointmentService.getAllProfessionalsSantiago() : appointmentService.getAllProfessionals(),
            branch === 'santiago' ? appointmentService.getAllCategoriesSantiago() : appointmentService.getAllCategories(),
            clientService.getAll()
          ]);
          setProfessionals(pros);
          setCategories(cats);
          setClients(Array.isArray(clientsData) ? clientsData : (clientsData.results || []));
        } catch (error) {
          console.error('Error cargando datos para formulario:', error);
        }
      };
      fetchData();
    }
  }, [showCreateModal, branch]);

  // Sin buscador por texto, no se requiere debounce

  const loadBookings = async () => {
    setLoading(true);
    try {
      let data;
      if (viewMode === 'active') {
        data = branch === 'santiago' 
          ? await appointmentService.getActiveBookingsSantiago() 
          : await appointmentService.getActiveBookings();
      } else {
        data = branch === 'santiago'
          ? await appointmentService.getHistoryBookingsSantiago()
          : await appointmentService.getHistoryBookings();
      }
      if (Array.isArray(data)) {
        // Ordenar descendente: Fechas más recientes (o futuras) primero
        data.sort((a: Appointment, b: Appointment) => {
          const dateA = new Date(`${a.date}T${a.start_time}`);
          const dateB = new Date(`${b.date}T${b.start_time}`);
          return dateB.getTime() - dateA.getTime();
        });
        setBookings(data);
      } else {
        setBookings([]);
      }
    } catch (err) {
      console.error('Error cargando reservas', err);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (booking: Appointment) => {
    if (window.confirm('¿Confirmar esta reserva?')) {
        try {
            if (branch === 'santiago') {
              await appointmentService.confirmBookingSantiago(booking.id);
            } else {
              await appointmentService.confirmBooking(booking.id);
            }
            await loadBookings();
        } catch (e) { 
            console.error(e); 
            alert('Error al confirmar la reserva'); 
        }
    }
  };

  const handleCancel = async (booking: Appointment) => {
    if (window.confirm('¿Cancelar esta reserva?')) {
        try {
            if (branch === 'santiago') {
              await appointmentService.cancelBookingSantiago(booking.id);
            } else {
              await appointmentService.cancelBooking(booking.id);
            }
            await loadBookings();
        } catch (e) { 
            console.error(e); 
            alert('Error al cancelar la reserva'); 
        }
    }
  };


  const handleEditClick = (booking: Appointment) => {
    setSelectedBooking(booking);
    setEditFormData({
      date: booking.date,
      start_time: booking.start_time,
      end_time: booking.end_time,
      customer_name: booking.customer_name || '',
      customer_phone: booking.customer_phone || '',
      customer_email: booking.customer_email || '',
    });
    setShowEditModal(true);
  };

  const handleDeleteClick = async (booking: Appointment) => {
    if (window.confirm(`¿Está seguro de que desea eliminar la cita de ${booking.customer_name || 'este cliente'}?`)) {
      try {
        if (branch === 'santiago') {
          await appointmentService.deleteBookingSantiago(booking.id);
        } else {
          await appointmentService.deleteBooking(booking.id);
        }
        alert('Cita eliminada exitosamente');
        await loadBookings(); // Recargar datos
      } catch (error) {
        console.error('Error al eliminar la cita:', error);
        alert('Error al eliminar la cita. Por favor, intente de nuevo.');
      }
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBooking) return;

    try {
      if (branch === 'santiago') {
        await appointmentService.updateBookingSantiago(selectedBooking.id, editFormData);
      } else {
        await appointmentService.updateBooking(selectedBooking.id, editFormData);
      }
      alert('Cita actualizada exitosamente');
      setShowEditModal(false);
      setSelectedBooking(null);
      await loadBookings(); // Recargar datos
    } catch (error) {
      console.error('Error al actualizar la cita:', error);
      alert('Error al actualizar la cita. Por favor, intente de nuevo.');
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedBooking(null);
  };

  // Lógica para crear reserva
  const handleCreateClick = () => {
    setCreateFormData({
      professional: '',
      date: '',
      start_time: '',
      end_time: '',
      services: [],
      customer_name: '',
      customer_phone: '',
      customer_email: '',
    });
    setSelectedClientId(null);
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
  };

  const calculateEndTime = (startTime: string, servicesIds: number[]) => {
    if (!startTime || servicesIds.length === 0) return startTime;
    
    let totalMinutes = 0;
    // Buscar duración de servicios seleccionados
    categories.forEach(cat => {
        cat.services.forEach(svc => {
            if (servicesIds.includes(svc.id)) {
                totalMinutes += svc.duration_minutes;
            }
        });
    });

    if (totalMinutes === 0) return startTime;

    const [hours, minutes] = startTime.split(':').map(Number);
    const date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes + totalMinutes);
    
    const endHours = date.getHours().toString().padStart(2, '0');
    const endMinutes = date.getMinutes().toString().padStart(2, '0');
    return `${endHours}:${endMinutes}`;
  };

  const handleServiceChange = (serviceId: number, checked: boolean) => {
    setCreateFormData(prev => {
      const newServices = checked 
        ? [...prev.services, serviceId]
        : prev.services.filter(id => id !== serviceId);
      
      // Recalcular hora de fin si hay hora de inicio
      const newEndTime = prev.start_time ? calculateEndTime(prev.start_time, newServices) : prev.end_time;

      return {
        ...prev,
        services: newServices,
        end_time: newEndTime
      };
    });
  };

  const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartTime = e.target.value;
    setCreateFormData(prev => ({
      ...prev,
      start_time: newStartTime,
      end_time: calculateEndTime(newStartTime, prev.services)
    }));
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return; // Prevent double submission
    setIsSubmitting(true);
    try {
      const payload = {
        ...createFormData,
        professional: createFormData.professional ? parseInt(createFormData.professional) : null,
      };
      if (selectedClientId) {
        (payload as any).user = selectedClientId;
      }
      
      if (branch === 'santiago') {
        await appointmentService.createBookingSantiago(payload);
      } else {
        await appointmentService.createBooking(payload);
      }
      alert('Reserva creada exitosamente');
      setShowCreateModal(false);
      await loadBookings();
    } catch (error: any) {
      console.error('Error al crear reserva:', error);
      const msg = error?.response?.data ? JSON.stringify(error.response.data) : 'Error desconocido';
      alert(`Error al crear la reserva: ${msg}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Reservas</h2>
            <p className="text-sm text-gray-600">Lista de todas las reservas realizadas por los clientes.</p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={handleCreateClick}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              <span>Nueva Reserva</span>
            </button>
            <div className="flex space-x-2 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setViewMode('active')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'active'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                Activas
              </button>
              <button
                onClick={() => setViewMode('history')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'history'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                Historial
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {loading ? (
          <div className="p-6 text-center">Cargando reservas...</div>
        ) : bookings.length === 0 ? (
          <div className="p-6 text-center text-gray-600">No hay reservas aún.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Horario</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Profesional</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {bookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span>{booking.date}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span>
                          {booking.start_time} - {booking.end_time}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div>
                        <div className="font-medium text-gray-900">{booking.customer_name || 'Sin nombre'}</div>
                        {booking.customer_phone && <div className="text-gray-500 text-xs">{booking.customer_phone}</div>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {booking.professional_detail ? (
                        <div className="flex items-center space-x-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span>{booking.professional_detail.full_name || `${booking.professional_detail.first_name} ${booking.professional_detail.last_name}`}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400">No asignado</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            booking.status === 'CONFIRMED' ? 'bg-green-100 text-green-800' :
                            booking.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                            booking.status === 'COMPLETED' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                        }`}>
                            {booking.status === 'CONFIRMED' ? 'Confirmada' :
                             booking.status === 'CANCELLED' ? 'Cancelada' :
                             booking.status === 'COMPLETED' ? 'Completada' : 'Pendiente'}
                        </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex flex-col">
                        <div className="flex items-center space-x-1">
                          <DollarSign className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">${Number(booking.total_price).toLocaleString('es-CL')}</span>
                        </div>
                        {(booking as any).booking_source === 'online' && (booking as any).has_deposit && (
                          <span className="text-xs text-green-600 font-medium mt-0.5">
                            Abono: ${((booking as any).deposit_amount || Number(booking.total_price) / 2).toLocaleString('es-CL')}
                          </span>
                        )}
                        {(booking as any).booking_source === 'manual' && (
                          <span className="text-xs text-gray-400 mt-0.5">Manual (sin abono)</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center space-x-2">
                        {/* Botones de estado para reservas pendientes */}
                        {booking.status === 'PENDING' && (
                            <>
                                <button
                                    onClick={() => handleConfirm(booking)}
                                    className="p-1 text-green-600 hover:bg-green-50 rounded"
                                    title="Confirmar"
                                >
                                    <Check className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => handleCancel(booking)}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                    title="Cancelar"
                                >
                                    <XCircle className="w-4 h-4" />
                                </button>
                            </>
                        )}
                        
                        {/* Permitir cancelar reservas confirmadas también */}
                        {booking.status === 'CONFIRMED' && (
                             <button
                                onClick={() => handleCancel(booking)}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                title="Cancelar reserva confirmada"
                            >
                                <XCircle className="w-4 h-4" />
                            </button>
                        )}

                        <button
                          onClick={() => handleEditClick(booking)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(booking)}
                          className="p-1 text-gray-600 hover:bg-gray-50 rounded"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de edición */}
      {showEditModal && selectedBooking && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeEditModal}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 relative max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Botón cerrar */}
            <button
              onClick={closeEditModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Título */}
            <h3 className="text-xl font-bold text-gray-900 mb-6 pr-8">
              Editar Reserva
            </h3>

            {/* Formulario de edición */}
            <form onSubmit={handleEditSubmit} className="space-y-4">
              {/* Información del profesional (solo lectura) */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-600 mb-1">Profesional asignado:</p>
                <p className="font-semibold text-gray-900">
                  {selectedBooking.professional_detail
                    ? `${selectedBooking.professional_detail.first_name} ${selectedBooking.professional_detail.last_name}`
                    : 'Sin asignar'}
                </p>
              </div>

              {/* Servicios (solo lectura) */}
              {selectedBooking.appointment_services && selectedBooking.appointment_services.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-600 mb-2">Servicios:</p>
                  <div className="space-y-1">
                    {selectedBooking.appointment_services.map((service, idx) => (
                      <p key={idx} className="text-sm font-medium text-gray-900">
                        • {service.name} ({service.duration_minutes} min)
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Fecha */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha
                </label>
                <input
                  type="date"
                  value={editFormData.date}
                  onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Hora de inicio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hora de inicio
                </label>
                <input
                  type="time"
                  min="08:00"
                  max="21:00"
                  value={editFormData.start_time}
                  onChange={(e) => setEditFormData({ ...editFormData, start_time: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Hora de fin */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hora de fin
                </label>
                <input
                  type="time"
                  min="08:00"
                  max="21:00"
                  value={editFormData.end_time}
                  onChange={(e) => setEditFormData({ ...editFormData, end_time: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Nombre del cliente */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del cliente
                </label>
                <input
                  type="text"
                  value={editFormData.customer_name}
                  onChange={(e) => setEditFormData({ ...editFormData, customer_name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nombre completo"
                />
              </div>

              {/* Teléfono del cliente */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Teléfono del cliente
                </label>
                <input
                  type="tel"
                  value={editFormData.customer_phone}
                  onChange={(e) => setEditFormData({ ...editFormData, customer_phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="+56 9 1234 5678"
                />
              </div>

              {/* Email del cliente */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email del cliente
                </label>
                <input
                  type="email"
                  value={editFormData.customer_email}
                  onChange={(e) => setEditFormData({ ...editFormData, customer_email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="cliente@ejemplo.com"
                />
              </div>

              {/* Botones */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Guardar cambios
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de creación */}
      {showCreateModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeCreateModal}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6 relative max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeCreateModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            <h3 className="text-xl font-bold text-gray-900 mb-6 pr-8">
              Nueva Reserva Manual
            </h3>

            <form onSubmit={handleCreateSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Profesional */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Profesional
                  </label>
                  <select
                    value={createFormData.professional}
                    onChange={(e) => setCreateFormData({ ...createFormData, professional: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    <option value="">Seleccionar profesional (opcional)</option>
                    {professionals.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.full_name || `${p.first_name} ${p.last_name}`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Fecha */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha
                  </label>
                  <input
                    type="date"
                    value={createFormData.date}
                    onChange={(e) => setCreateFormData({ ...createFormData, date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Hora Inicio */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hora de inicio
                  </label>
                  <input
                    type="time"
                    min="08:00"
                    max="21:00"
                    value={createFormData.start_time}
                    onChange={handleStartTimeChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    required
                  />
                </div>

                {/* Hora Fin */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hora de fin (Estimada)
                  </label>
                  <input
                    type="time"
                    min="08:00"
                    max="21:00"
                    value={createFormData.end_time}
                    onChange={(e) => setCreateFormData({ ...createFormData, end_time: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              {/* Servicios */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Servicios
                </label>
                {/* Filtro por categoría */}
                <div className="mb-3 grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
                  <div className="md:col-span-2">
                    <select
                      value={categoryFilterId ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setCategoryFilterId(v ? parseInt(v) : null);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    >
                      <option value="">Filtrar por categoría (opcional)</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={() => { setCategoryFilterId(null); setServiceSearchText(''); }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                    >
                      Limpiar filtros
                    </button>
                  </div>
                </div>
                {/* Buscador de servicios por texto */}
                <div className="mb-3">
                  <input
                    type="text"
                    value={serviceSearchText}
                    onChange={(e) => setServiceSearchText(e.target.value)}
                    placeholder="Buscar servicio por nombre..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
                <div className="border border-gray-300 rounded-lg p-4 max-h-60 overflow-y-auto">
                  {(categoryFilterId ? categories.filter(c => c.id === categoryFilterId) : categories)
                    .map(cat => {
                      // Filtrar servicios por texto de búsqueda y profesional seleccionado
                      const filteredServices = cat.services.filter(svc => {
                        const matchesText = svc.name.toLowerCase().includes(serviceSearchText.toLowerCase());
                        // Si hay un profesional seleccionado, verificar que el servicio lo incluya
                        // svc.professionals es un array de IDs (number[])
                        const matchesProfessional = !createFormData.professional || 
                          (svc.professionals && svc.professionals.includes(parseInt(createFormData.professional)));
                        
                        return matchesText && matchesProfessional;
                      });
                      
                      // No mostrar categoría si no tiene servicios que coincidan
                      if (filteredServices.length === 0) return null;
                      
                      return (
                        <div key={cat.id} className="mb-4 last:mb-0">
                          <h4 className="font-medium text-gray-900 mb-2 bg-white py-1">
                            {cat.name}
                          </h4>
                          <div className="space-y-2 pl-2">
                            {filteredServices.map(svc => (
                              <label key={svc.id} className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-1 rounded">
                                <input
                                  type="checkbox"
                                  checked={createFormData.services.includes(svc.id)}
                                  onChange={(e) => handleServiceChange(svc.id, e.target.checked)}
                                  className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                                />
                                <div className="flex-1">
                                  <span className="text-sm text-gray-700 font-medium">{svc.name}</span>
                                  <span className="text-xs text-gray-500 ml-2">
                                    ({svc.duration_minutes} min - ${Number(svc.price).toLocaleString('es-CL')})
                                  </span>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Selecciona los servicios para calcular automáticamente la hora de fin.
                </p>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <h4 className="font-medium text-gray-900 mb-4">Datos del Cliente</h4>

                {/* Buscador de clientes frecuentes */}
                <div className="mb-6 relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Buscar Cliente Frecuente
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={clientSearchText}
                      onChange={(e) => {
                        setClientSearchText(e.target.value);
                        setShowClientSuggestions(true);
                      }}
                      onFocus={() => setShowClientSuggestions(true)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="Buscar por nombre, email o teléfono..."
                    />
                    <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  </div>
                  
                  {showClientSuggestions && clientSearchText && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {clients
                        .filter(client => 
                          (client.first_name + ' ' + client.last_name).toLowerCase().includes(clientSearchText.toLowerCase()) ||
                          client.email.toLowerCase().includes(clientSearchText.toLowerCase()) ||
                          (client.phone_number && client.phone_number.includes(clientSearchText))
                        )
                        .map(client => (
                          <button
                            key={client.id}
                            type="button"
                            onClick={() => {
                              setCreateFormData({
                                ...createFormData,
                                customer_name: `${client.first_name} ${client.last_name}`.trim(),
                                customer_email: client.email,
                                customer_phone: client.phone_number || ''
                              });
                              setSelectedClientId(client.id);
                              setClientSearchText('');
                              setShowClientSuggestions(false);
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                          >
                            <div className="font-medium text-gray-900">
                              {client.first_name} {client.last_name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {client.email} {client.phone_number && `• ${client.phone_number}`}
                            </div>
                          </button>
                        ))}
                        {clients.filter(client => 
                          (client.first_name + ' ' + client.last_name).toLowerCase().includes(clientSearchText.toLowerCase()) ||
                          client.email.toLowerCase().includes(clientSearchText.toLowerCase()) ||
                          (client.phone_number && client.phone_number.includes(clientSearchText))
                        ).length === 0 && (
                          <div className="px-4 py-2 text-sm text-gray-500">
                            No se encontraron clientes
                          </div>
                        )}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre
                    </label>
                    <input
                      type="text"
                      value={createFormData.customer_name}
                      onChange={(e) => setCreateFormData({ ...createFormData, customer_name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="Nombre completo"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      value={createFormData.customer_phone}
                      onChange={(e) => setCreateFormData({ ...createFormData, customer_phone: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="+56 9 1234 5678"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={createFormData.customer_email}
                      onChange={(e) => setCreateFormData({ ...createFormData, customer_email: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="cliente@ejemplo.com"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Creando...' : 'Crear Reserva'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingsList;
